import { useContext, useMemo } from "react";
import { AuthContext } from "../../../providers/AuthProvider";
import { useShiftLocationsApiService } from "../../time-attendance/services/api/ShiftLocationsApiService";
import { getDateRangeForFilter } from "../../time-attendance/utils/dateRangeUtils";
import { ReportScope } from "../services/model/ReportScope";

export interface AvailableShift {
  name: string;
  code: string;
}

export interface UseAvailableShiftsForScopeResult {
  shifts: AvailableShift[];
  isLoading: boolean;
}

/**
 * Derives the distinct ShiftConfigs (`{code, name}`) available for a given
 * report scope by reading `LocationSummary.shift_configs` from the existing
 * T&A overview endpoint. Roster scopes look at one location; site scopes
 * dedupe across every location under the site; region (Site Group) scopes
 * dedupe across all locations (the slim overview payload carries no group id,
 * so we offer the employer-wide union — the backend re-applies the shift_name
 * filter once it resolves the group's rosters).
 *
 * Note: uses a fixed "this-month" date range only because the overview API
 * requires one. Shift configs in LocationSummary are static metadata, so the
 * specific window doesn't affect which shifts get returned.
 */
const useAvailableShiftsForScope = (
  scope: ReportScope
): UseAvailableShiftsForScopeResult => {
  const auth = useContext(AuthContext);
  const { from, to } = useMemo(() => getDateRangeForFilter("this-month"), []);

  // Same slim payload as the reports page picker — React Query dedupes on
  // the shared cache key (employer + this-month + summary=true).
  const { data, isLoading } = useShiftLocationsApiService({
    employer: auth?.employer?.id,
    date_from: from,
    date_to: to,
    summary: true,
  });

  const shifts = useMemo<AvailableShift[]>(() => {
    const locations = data?.results ?? [];
    let relevant;
    if (scope.kind === "roster") {
      relevant = locations.filter((l) => l.id === scope.id);
    } else if (scope.kind === "site") {
      relevant = locations.filter((l) => l.site === scope.id);
    } else {
      // region (Site Group): the slim overview payload doesn't carry the
      // group id, so we can't narrow to the group here. Offer the union of
      // every location's shifts. Shift configs are standardised (Day/Night),
      // and the backend re-applies the shift_name filter after resolving the
      // group's rosters — a shift absent from the group just yields no rows.
      relevant = locations;
    }

    const byName = new Map<string, AvailableShift>();
    relevant.forEach((loc) => {
      (loc.shift_configs ?? []).forEach((cfg) => {
        if (!cfg.name) return;
        if (!byName.has(cfg.name)) {
          byName.set(cfg.name, { name: cfg.name, code: cfg.code });
        }
      });
    });

    return Array.from(byName.values()).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }, [data, scope]);

  return { shifts, isLoading };
};

export default useAvailableShiftsForScope;
